/* eslint-disable complexity */
import {
    EscalationType,
    PaymentArrears,
    PaySurvivingDependantType,
    TaxFreeType,
    usePensionOptionsStore,
    ValueProtectOrGuarantee,
} from "../stores/pensionOptions";
import {
    MeasurementUnitLength,
    MeasurementUnitType,
    MeasurementUnitWeight,
    SmokerStatus,
    useAboutYouStore,
} from "../stores/aboutYou";
import { useYourDetailsStore } from "../stores/yourDetails";
import { useFinallyStore } from "../stores/finally";
import { determineDevice } from "./devices";
import {
    PartialForm,
    QuoteRequest,
} from "../entities/quote";
import { MedicalCondition } from "@/entities/conditions";
import { RelationshipToCustomer, useDependantAboutYouStore } from "../stores/dependantAboutYou";
import { ensureDefined } from "./types";
import {
    ApplicantMobility,
    DressBathYourself,
    ApplicantFeeding,
    ApplicantProgression,
    ApplicantToilet,
    useDailyActivitiesStore,
    ApplicantTransferring,
} from "@/stores/dailyActivities";
import { useDependantDailyActivitiesStore } from "@/stores/dependantDailyActivities";
import Cookies from "universal-cookie";
import { gender } from "@/composables/aboutYou";
import { AnnuitiesPremium, PolicyType } from "@/entities/premium";

const cookies = new Cookies();

export enum RequestType {
    Quote = 1,
    PartialForm = 2
}

type ImperialHeight = {
    feet: number,
    inches: number,
}

type ImperialWeight = {
    stone: number,
    pounds: number,
}

export const mapDataToPartialForm = (): PartialForm => {
    const pensionOptionsStore = usePensionOptionsStore();
    const aboutYouStore = useAboutYouStore();
    const yourDetailsStore = useYourDetailsStore();
    const dependantAboutYouStore = useDependantAboutYouStore();
    const finallyStore = useFinallyStore();
    const dailyActivitiesStore = useDailyActivitiesStore();
    const dependantDailyActivitiesStore = useDependantDailyActivitiesStore();

    const pensionOptions = pensionOptionsStore.getProductInformation;
    const lifestyle = aboutYouStore.getLifeStyleInformationForPartial;
    const { maritalStatus } = yourDetailsStore.getCustomerDetails;

    const applicantDailyActivities = dailyActivitiesStore.getDailyActivities;
    const dependantDailyActivities = dependantDailyActivitiesStore.getDailyActivities;
    const applicantMedicalConditions = aboutYouStore.getMedicalConditions;
    const dependantMedicalConditions = dependantAboutYouStore.getMedicalConditions;

    const partialForm: PartialForm = {
        customer: {
            ...yourDetailsStore.getCustomerDetails,
            // @ts-expect-error not playing ball...
            maritalStatus: maritalStatus || undefined,
            lifestyle,
            medical: aboutYouStore.medical ? applicantMedicalConditions : undefined,
            dailyActivities: aboutYouStore.medical ? applicantDailyActivities : undefined,
        },
        dependant: {},
        product: {
            paymentFrequency: pensionOptions.paymentFrequency ?? undefined,
            proportional: pensionOptions.proportional,
            paymentInArrears: pensionOptions.paymentInArrears
                ? pensionOptions.paymentInArrears === PaymentArrears.InArrears
                : undefined,
            totalFund: pensionOptions.totalFund === 0
                ? undefined
                : pensionOptions.totalFund,
            taxFree: pensionOptions.taxFree,
            annuityExceedMaximum: pensionOptions.annuityExceedMaximum,
            acknowledgementILSA: pensionOptions.acknowledgementILSA,
            escalation: pensionOptions?.escalation?.type !==
            undefined
                ? pensionOptions.escalation
                : undefined,
            guarantee: pensionOptions.guarantee,
            paySurvivingDependant: pensionOptions.paySurvivingDependant,
            overlap: pensionOptions.overlap,
            consent: finallyStore.consent ?? undefined,
            pensionEligibility: {
                pensionType: pensionOptions.pensionType,
                fundTransferred: pensionOptions.fundTransferred,
                guaranteeAnnuityRate: pensionOptions.guaranteeAnnuityRate,
                moreThanTwentyFive: pensionOptions.moreThanTwentyFive,
            },
        },
        affiliateData: {
            ...finallyStore.getMarketingInformation,
        },
        device: determineDevice(navigator, screen),
        affiliate: {
            name: "AnnuityReady",
            number: 228,
            sourceId: null,
        },
        tags: {},
    };

    if (pensionOptionsStore.spouseDependant) {
        const dependant = dependantAboutYouStore.getPartialDependantInformation;
        partialForm.dependant = {
            ...dependant,
            medical: dependantAboutYouStore.medical ? dependantMedicalConditions : undefined,
            dailyActivities: dependantAboutYouStore.medical
                ? dependantDailyActivities
                : undefined,
        };
    }

    return partialForm;
};

export const mapDataToQuoteRequest = (): QuoteRequest => {
    const pensionOptionsStore = usePensionOptionsStore();
    const aboutYouStore = useAboutYouStore();
    const yourDetailsStore = useYourDetailsStore();
    const dependantAboutYouStore = useDependantAboutYouStore();
    const finallyStore = useFinallyStore();
    const dailyActivitiesStore = useDailyActivitiesStore();
    const dependantDailyActivitiesStore = useDependantDailyActivitiesStore();

    const pensionOptions = pensionOptionsStore.getProductInformation;

    const lifestyle = aboutYouStore.getLifeStyleInformation;

    const { maritalStatus } = yourDetailsStore.getCustomerDetails;

    const gclid = cookies.get("clickid_gclid");

    const tags: Record<string, string> = {};

    if (gclid) {
        tags.gclid = gclid;
    }

    const applicantDailyActivities = dailyActivitiesStore.getDailyActivities;
    const dependantDailyActivities = dependantDailyActivitiesStore.getDailyActivities;
    const applicantMedicalConditions = aboutYouStore.getMedicalConditions;
    const dependantMedicalConditions = dependantAboutYouStore.getMedicalConditions;

    const quoteRequest: QuoteRequest = {
        appointmentChoice: null,
        customer: {
            ...yourDetailsStore.getCustomerDetails,
            title: yourDetailsStore.title ?? null,
            maritalStatus: maritalStatus ?? null,
            lifestyle,
            medical: aboutYouStore.medical ? applicantMedicalConditions : undefined,
            dailyActivities: aboutYouStore.medical ? applicantDailyActivities : undefined,
        },
        dependant: undefined,
        product: {
            paymentFrequency: ensureDefined(pensionOptions.paymentFrequency),
            proportional: pensionOptions.proportional,
            paymentInArrears: ensureDefined(pensionOptions.paymentInArrears) === PaymentArrears.InArrears,
            totalFund: ensureDefined(pensionOptions.totalFund),
            taxFree: ensureDefined(pensionOptions.taxFree),
            escalation: ensureDefined(pensionOptions.escalation),
            guarantee: ensureDefined(pensionOptions.guarantee),
            paySurvivingDependant: ensureDefined(pensionOptions.paySurvivingDependant),
            overlap: pensionOptions.overlap,
            consent: !!finallyStore.consent,
            pensionEligibility: {
                pensionType: pensionOptions.pensionType,
                fundTransferred: pensionOptions.fundTransferred,
                guaranteeAnnuityRate: pensionOptions.guaranteeAnnuityRate,
                moreThanTwentyFive: pensionOptions.moreThanTwentyFive,
            },
            annuityExceedMaximum: pensionOptions.annuityExceedMaximum,
            acknowledgementILSA: pensionOptions.acknowledgementILSA,
        },
        affiliateData: finallyStore.getMarketingInformation,
        device: determineDevice(navigator, screen),
        affiliate: {
            name: "AnnuityReady",
            number: 228,
            sourceId: null,
        },
        tags,
        async: true,
    };

    if (pensionOptionsStore.spouseDependant) {
        const dependant = dependantAboutYouStore.getDependantInformation;
        quoteRequest.dependant = {
            relationshipToCustomer: dependant.relationshipToCustomer ?? undefined,
            title: dependant.title || undefined,
            forename: dependant.forename || undefined,
            surname: dependant.surname || undefined,
            dob: ensureDefined(dependant.dob),
            sex: dependant.sex ?? gender(dependant.title),
            maritalStatus: dependant.maritalStatus,
            address: {
                line1: null,
                line2: null,
                city: null,
                county: null,
                postcode: ensureDefined(dependant.address?.postcode),
            },
            lifestyle: dependant.lifestyle
                ? {
                    ...dependant.lifestyle,
                }
                : null,
            medical: dependantAboutYouStore.medical
                ? dependantMedicalConditions
                : undefined,
            dailyActivities: dependantAboutYouStore.medical
                ? dependantDailyActivities
                : undefined,
        };
    }
    return quoteRequest;
};

export const mapDataFromQuoteRequest = (formData: PartialForm | QuoteRequest): void => {
    const pensionOptionsStore = usePensionOptionsStore();
    const aboutYouStore = useAboutYouStore();
    const yourDetailsStore = useYourDetailsStore();
    const dependantAboutYouStore = useDependantAboutYouStore();
    const dailyActivitiesStore = useDailyActivitiesStore();
    const dependantDailyActivitiesStore = useDependantDailyActivitiesStore();
    const finallyStore = useFinallyStore();

    yourDetailsStore.quoteLoaded = true;

    const customer = formData.customer;
    const product = formData.product;
    const lifestyle = formData.customer?.lifestyle;
    const dependant = formData.dependant;
    const dependantLifeStyle = dependant?.lifestyle;

    yourDetailsStore.title = customer.title;
    yourDetailsStore.forename = customer.forename;
    yourDetailsStore.surname = customer.surname;
    yourDetailsStore.dob = customer.dob;
    yourDetailsStore.email = customer.email;
    yourDetailsStore.phone = customer.phone ?? "";
    yourDetailsStore.maritalStatus = customer.maritalStatus;
    yourDetailsStore.postcode = customer.address?.postcode ?? "";
    yourDetailsStore.sex = customer.sex ?? null;

    if (product && Object.keys(product).length > 0) {
        pensionOptionsStore.pensionType = product.pensionEligibility?.pensionType ?? null;
        pensionOptionsStore.fundTransferred = product.pensionEligibility?.fundTransferred ?? null;
        pensionOptionsStore.guaranteeAnnuityRate = product.pensionEligibility?.guaranteeAnnuityRate ?? null;
        pensionOptionsStore.moreThanTwentyFive = product.pensionEligibility?.moreThanTwentyFive ?? null;
        pensionOptionsStore.totalFund = product.totalFund ?? null;
        pensionOptionsStore.taxFreeSumFull = product.taxFree?.type ? product.taxFree.type === TaxFreeType.Full : null;
        pensionOptionsStore.taxFreeSumSpecific =
            product.taxFree?.type === TaxFreeType.Absolute
                ? product.taxFree?.value
                : null;
        pensionOptionsStore.annuityExceedMaximum = product.annuityExceedMaximum ?? null;
        pensionOptionsStore.acknowledgementILSA = product.acknowledgementILSA ?? null;
        pensionOptionsStore.escalation = product.escalation?.type !== undefined
            ? product.escalation?.type !== undefined && product.escalation.type !== EscalationType.None
            : null;
        pensionOptionsStore.escalationType = product.escalation?.type ?? null;
        pensionOptionsStore.escalationAmount =
            product.escalation?.type === EscalationType.Fixed ? formatEscalationAmount(product.escalation?.value) : "";
        pensionOptionsStore.paymentFrequency = product.paymentFrequency ?? null;
        pensionOptionsStore.paymentArrears = product.paymentInArrears !== undefined
            ? product.paymentInArrears ? PaymentArrears.InArrears : PaymentArrears.InAdvance
            : null;
        pensionOptionsStore.proportional = product.proportional;
        pensionOptionsStore.spouseDependant = product.paySurvivingDependant?.type
            ? product.paySurvivingDependant?.type === PaySurvivingDependantType.Fraction
            : null;
        pensionOptionsStore.valueProtectOrGuarantee = product.guarantee?.type ?? null;
        pensionOptionsStore.spouseDependantPercent =
            product.paySurvivingDependant?.type === PaySurvivingDependantType.Fraction
                ? product.paySurvivingDependant.value.toString()
                : "";
        pensionOptionsStore.overlap = product.overlap;
        pensionOptionsStore.valueProtectionBasis =
            product.guarantee?.type === ValueProtectOrGuarantee.ValueProtection ? product.guarantee.endingAt : "";
        pensionOptionsStore.valueProtectionPercent =
            product.guarantee?.type === ValueProtectOrGuarantee.ValueProtection
                ? product.guarantee.fraction.toString()
                : "";
        pensionOptionsStore.guaranteePeriodYears =
            product.guarantee?.type === ValueProtectOrGuarantee.GuaranteePeriod
                ? product.guarantee.years.toString()
                : "";
    }

    if (lifestyle) {
        if ("smoker" in lifestyle) {
            aboutYouStore.smoker = lifestyle.smoker ?? SmokerStatus.Never;
            if ("smoking" in lifestyle && lifestyle.smoking) {
                aboutYouStore.smokingStart = lifestyle.smoking.started ?? "";
                // eslint-disable-next-line max-depth
                if ("ended" in lifestyle.smoking) {
                    aboutYouStore.smokingEnd = lifestyle.smoking.ended;
                }
                aboutYouStore.cigarettes = lifestyle.smoking.cigarettesPerDay > 0;
                aboutYouStore.cigarettesPerDay = lifestyle.smoking.cigarettesPerDay;
                aboutYouStore.cigars = lifestyle.smoking.cigarsPerDay > 0;
                aboutYouStore.cigarsPerDay = lifestyle.smoking.cigarsPerDay;
                aboutYouStore.rollingTobacco = lifestyle.smoking.rollingTobaccoGramsPerWeek > 0;
                aboutYouStore.rollingTobaccoPerWeek = lifestyle.smoking.rollingTobaccoGramsPerWeek;
                aboutYouStore.pipeTobacco = lifestyle.smoking.pipeTobaccoGramsPerWeek > 0;
                aboutYouStore.pipeTobaccoPerWeek = lifestyle.smoking.pipeTobaccoGramsPerWeek;
            }
        }

        if ("alcoholUnitsPerWeek" in lifestyle && lifestyle.alcoholUnitsPerWeek !== undefined) {
            aboutYouStore.alcohol = lifestyle.alcoholUnitsPerWeek;
        }
        if ("height" in lifestyle && lifestyle.height) {
            aboutYouStore.heightUnit = getUnit(lifestyle.height.unit);
            if (lifestyle.height.unit === MeasurementUnitLength.Centimetres) {
                aboutYouStore.heightCentimetres = lifestyle.height.value;
            } else {
                const height = convertInchesToFootAndInches(lifestyle.height.value);
                aboutYouStore.heightFeet = height.feet;
                aboutYouStore.heightInches = height.inches;
            }
        }
        if ("weight" in lifestyle && lifestyle.weight) {
            aboutYouStore.weightUnit = getUnit(lifestyle.weight.unit);
            if (aboutYouStore.weightUnit === MeasurementUnitType.Metric) {
                aboutYouStore.weightKg = lifestyle.weight.value;
            } else {
                const weight = convertPoundsToStoneAndPounds(lifestyle.weight?.value);
                aboutYouStore.weightStone = weight.stone;
                aboutYouStore.weightPounds = weight.pounds;
            }
        }
        if ("waist" in lifestyle && lifestyle.waist) {
            aboutYouStore.waistUnit = getUnit(lifestyle.waist.unit);
            if (aboutYouStore.waistUnit === MeasurementUnitType.Metric) {
                aboutYouStore.waistCentimetres = lifestyle.waist.value;
            } else {
                aboutYouStore.waistInches = lifestyle.waist.value;
            }
        }
    }

    if (customer?.medical && customer?.medical.length > 0) {
        aboutYouStore.medical = true;
        aboutYouStore.selectedMedicalConditions = customer.medical as MedicalCondition[];

        if (customer.dailyActivities && Object.keys(customer.dailyActivities).length > 0) {
            dailyActivitiesStore.dressYourself = customer.dailyActivities.dressYourself as DressBathYourself;

            dailyActivitiesStore.applicantMobility = customer.dailyActivities.applicantMobility as ApplicantMobility;

            dailyActivitiesStore.applicantTransferring =
            customer.dailyActivities.applicantTransferring as ApplicantTransferring;

            dailyActivitiesStore.applicantBladder = customer.dailyActivities.applicantBladder as ApplicantToilet;

            dailyActivitiesStore.applicantBowels = customer.dailyActivities.applicantBowels as ApplicantToilet;

            dailyActivitiesStore.applicantBathing = customer.dailyActivities.applicantBathing as DressBathYourself;

            dailyActivitiesStore.applicantFeeding = customer.dailyActivities.applicantFeeding as ApplicantFeeding;

            dailyActivitiesStore.applicantProgression =
            customer.dailyActivities.applicantProgression as ApplicantProgression;
        }
    } else if (customer?.medical && customer.medical.length === 0) {
        aboutYouStore.medical = false;
    }

    if (dependant && Object.keys(dependant).length > 0) {
        if ("title" in dependant) {
            dependantAboutYouStore.title = ensureDefined(dependant?.title);
        }
        if ("forename" in dependant) {
            dependantAboutYouStore.forename = ensureDefined(dependant?.forename);
        }
        if ("surname" in dependant) {
            dependantAboutYouStore.surname = ensureDefined(dependant?.surname);
        }
        if ("dob" in dependant) {
            dependantAboutYouStore.dob = ensureDefined(dependant?.dob);
        }
        if ("sex" in dependant) {
            dependantAboutYouStore.sex = ensureDefined(dependant?.sex);
        }
        if ("address" in dependant) {
            dependantAboutYouStore.postcode = ensureDefined(dependant?.address?.postcode);
        }
        if ("maritalStatus" in dependant) {
            dependantAboutYouStore.maritalStatus = dependant?.maritalStatus ?? null;
        }
        if ("relationshipToCustomer" in dependant) {
            dependantAboutYouStore.relationshipToCustomer =
                ensureDefined(dependant?.relationshipToCustomer as RelationshipToCustomer);
        }
    }

    if (dependantLifeStyle) {
        if ("smoker" in dependantLifeStyle) {
            dependantAboutYouStore.smoker = dependantLifeStyle.smoker ?? SmokerStatus.Never;
            if ("smoking" in dependantLifeStyle && dependantLifeStyle.smoking) {
                dependantAboutYouStore.smokingStart = dependantLifeStyle.smoking.started ?? "";
                // eslint-disable-next-line max-depth
                if ("ended" in dependantLifeStyle.smoking) {
                    dependantAboutYouStore.smokingEnd = dependantLifeStyle.smoking.ended;
                }
                dependantAboutYouStore.cigarettes = dependantLifeStyle.smoking.cigarettesPerDay > 0;
                dependantAboutYouStore.cigarettesPerDay = dependantLifeStyle.smoking.cigarettesPerDay;
                dependantAboutYouStore.cigars = dependantLifeStyle.smoking.cigarsPerDay > 0;
                dependantAboutYouStore.cigarsPerDay = dependantLifeStyle.smoking.cigarsPerDay;
                dependantAboutYouStore.rollingTobacco = dependantLifeStyle.smoking.rollingTobaccoGramsPerWeek > 0;
                dependantAboutYouStore.rollingTobaccoPerWeek = dependantLifeStyle.smoking.rollingTobaccoGramsPerWeek;
                dependantAboutYouStore.pipeTobacco = dependantLifeStyle.smoking.pipeTobaccoGramsPerWeek > 0;
                dependantAboutYouStore.pipeTobaccoPerWeek = dependantLifeStyle.smoking.pipeTobaccoGramsPerWeek;
            }
        }

        if ("alcoholUnitsPerWeek" in dependantLifeStyle && dependantLifeStyle.alcoholUnitsPerWeek !== undefined) {
            dependantAboutYouStore.alcohol = dependantLifeStyle.alcoholUnitsPerWeek;
        }
        if ("height" in dependantLifeStyle && dependantLifeStyle.height) {
            dependantAboutYouStore.heightUnit = getUnit(dependantLifeStyle.height.unit);
            if (dependantLifeStyle.height.unit === MeasurementUnitLength.Centimetres) {
                dependantAboutYouStore.heightCentimetres = dependantLifeStyle.height.value;
            } else {
                const height = convertInchesToFootAndInches(dependantLifeStyle.height.value);
                dependantAboutYouStore.heightFeet = height.feet;
                dependantAboutYouStore.heightInches = height.inches;
            }
        }
        if ("weight" in dependantLifeStyle && dependantLifeStyle.weight) {
            dependantAboutYouStore.weightUnit = getUnit(dependantLifeStyle.weight.unit);
            if (dependantAboutYouStore.weightUnit === MeasurementUnitType.Metric) {
                dependantAboutYouStore.weightKg = dependantLifeStyle.weight.value;
            } else {
                const weight = convertPoundsToStoneAndPounds(dependantLifeStyle.weight?.value);
                dependantAboutYouStore.weightStone = weight.stone;
                dependantAboutYouStore.weightPounds = weight.pounds;
            }
        }
        if ("waist" in dependantLifeStyle && dependantLifeStyle.waist) {
            dependantAboutYouStore.waistUnit = getUnit(dependantLifeStyle.waist.unit);
            if (dependantAboutYouStore.waistUnit === MeasurementUnitType.Metric) {
                dependantAboutYouStore.waistCentimetres = dependantLifeStyle.waist.value;
            } else {
                dependantAboutYouStore.waistInches = dependantLifeStyle.waist.value;
            }
        }
    }

    if (dependant && "medical" in dependant && dependant.medical && dependant.medical.length > 0) {
        dependantAboutYouStore.medical = true;
        dependantAboutYouStore.selectedMedicalConditions = dependant.medical as MedicalCondition[];
        if (dependant.dailyActivities && Object.keys(dependant.dailyActivities).length > 0) {
            dependantDailyActivitiesStore.dressYourself =
            dependant.dailyActivities.dressYourself as DressBathYourself;

            dependantDailyActivitiesStore.applicantMobility =
            dependant.dailyActivities.applicantMobility as ApplicantMobility;

            dependantDailyActivitiesStore.applicantTransferring =
            dependant.dailyActivities.applicantTransferring as ApplicantTransferring;

            dependantDailyActivitiesStore.applicantBladder =
            dependant.dailyActivities.applicantBladder as ApplicantToilet;

            dependantDailyActivitiesStore.applicantBowels =
            dependant.dailyActivities.applicantBowels as ApplicantToilet;

            dependantDailyActivitiesStore.applicantBathing =
            dependant.dailyActivities.applicantBathing as DressBathYourself;

            dependantDailyActivitiesStore.applicantFeeding =
            dependant.dailyActivities.applicantFeeding as ApplicantFeeding;

            dependantDailyActivitiesStore.applicantProgression =
            dependant.dailyActivities.applicantProgression as ApplicantProgression;
        }
    } else if (dependant && "medical" in dependant && dependant.medical && dependant.medical.length === 0) {
        dependantAboutYouStore.medical = false;
    }
    if (product.consent !== undefined) {
        finallyStore.consent = product.consent;
    }
};

function getUnit(unit: string): MeasurementUnitType {
    return unit === MeasurementUnitLength.Centimetres || unit === MeasurementUnitWeight.Kilogram
        ? MeasurementUnitType.Metric
        : MeasurementUnitType.Imperial;
}

export const convertHeightToInches = (feet: number, inches: number): number => {
    return (feet ?? 0) * 12 + (inches ?? 0);
};

export function formatEscalationAmount(amount: number): string {
    return Number.isInteger(amount) ? `${amount}.0` : amount.toString();
}

export function convertInchesToFootAndInches(inches: number): ImperialHeight {
    const foot = inches / 12;
    const totalInches = (foot - Math.floor(foot)) * 12;
    return {
        feet: Math.floor(foot) ?? 0,
        inches: Math.round(totalInches) ?? 0,
    };
}

export const convertWeightToPounds = (stone: number, pounds: number): number => {
    return stone * 14 + pounds;
};

export function convertPoundsToStoneAndPounds(pounds: number): ImperialWeight {
    const stone = pounds / 14;
    const totalPounds = (stone - Math.floor(stone)) * 14;
    return {
        stone: Math.floor(stone) ?? 0,
        pounds: Math.round(totalPounds) ?? 0,
    };
}

export const stonePoundsToKilograms = (stone: number, pounds: number): string => {
    return (Number(`${stone}.${pounds}`) * 6.35029318).toFixed(1);
};

export const kilogramsToStonePounds = (kilogram: number): ImperialWeight => {
    const totalStone = Math.round(kilogram / 6.36);
    const totalPounds = ((kilogram % 6.36) * 2.205).toFixed(2);
    return {
        stone: Number(totalStone.toString()),
        pounds: Number(totalPounds.toString()),
    };
};

export const footInchesToCentimetres = (feet: number, inches: number): number => {
    return (feet * 30.48) + (inches * 2.54);
};

export const centimetresToFootInches = (centimetres: number): ImperialHeight => {
    return convertInchesToFootAndInches(centimetres / 2.54);
};

export const centimetresToInches = (centimetres: number): string => {
    return Math.round(centimetres * 0.393701).toString();
};

export const inchesToCentimetres = (inches: number) => {
    return (inches * 2.54).toFixed(2);
};

function getTotalAnnualPension(premium: AnnuitiesPremium): number {
    if (premium.policy.type === PolicyType.Error) {
        return 0;
    }
    return Number(premium.policy.totalAnnualPension);
}

/**
 * Sorts an array of annuity premiums in descending order based on the total annual pension.
 * Optionally limits the number of premiums returned to the specified limit.
 *
 * @param {AnnuitiesPremium[]} premiums - The array of annuity premiums to be sorted.
 * @param {number} [premiumLimit] - The maximum number of sorted premiums to return.
 * If not specified, all sorted premiums are returned.
 * @returns {AnnuitiesPremium[]} An array of sorted annuity premiums, limited to the specified number if provided.
 */
export function sortedPremiums(premiums: AnnuitiesPremium[], premiumLimit?: number): AnnuitiesPremium[] {
    if (premiums?.length > 0) {
        const sorted = premiums.sort((premium1, premium2) => {
            const totalAnnualPension1 = getTotalAnnualPension(premium1);
            const totalAnnualPension2 = getTotalAnnualPension(premium2);

            return totalAnnualPension2 - totalAnnualPension1;
        });
        if (premiumLimit !== undefined && premiumLimit >= 0) {
            return sorted.slice(0, premiumLimit);
        }
        return sorted;
    }
    return [];
}
